import React, { useState, useEffect } from 'react';
import {
  FolderKanban,
  Ticket,
  Clock,
  AlertCircle,
  Receipt,
  TrendingUp,
  DollarSign,
  CheckCircle2,
  Building2,
  ArrowUpRight,
  Plus,
  Layers,
  ClipboardCheck,
  Hammer,
  Boxes,
  Smartphone,
  ChevronRight,
  ActivitySquare,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext.tsx';

interface DashboardPageProps {
  onNavigate: (route: string, param?: string) => void;
  mobileMode?: boolean;
}

const DEFAULT_PROJECTS = [
  {
    id: 1,
    projectNumber: 'NB-PRJ-2026-001',
    projectName: 'UBL Clifton Branch Comprehensive Renovation',
    branchName: 'UBL Clifton Block 5 Branch',
    branchCode: 'UBL-0142',
    contractValue: '4850000',
    actualCost: '2760000',
    status: 'IN_PROGRESS',
  },
  {
    id: 2,
    projectNumber: 'NB-PRJ-2026-002',
    projectName: 'UBL I.I Chundrigar Road Main Office Ceiling & LED Lighting Retrofit',
    branchName: 'UBL Main Branch (Head Office)',
    branchCode: 'UBL-0001',
    contractValue: '3200000',
    actualCost: '1890000',
    status: 'COMPLETED',
  },
  {
    id: 3,
    projectNumber: 'NB-PRJ-2026-003',
    projectName: 'UBL Gulberg III ATM Lobby & Glass Facade',
    branchName: 'UBL Gulberg III Branch',
    branchCode: 'UBL-0451',
    contractValue: '2150000',
    actualCost: '950000',
    status: 'PLANNING',
  },
];

const DEFAULT_TICKETS = [
  {
    id: 1,
    ticketNumber: 'TKT-2026-001',
    subject: 'Emergency Server Room AC Water Leakage & False Ceiling Damage',
    branchName: 'UBL Blue Area Branch',
    priority: 'HIGH',
    status: 'NEW',
  },
  {
    id: 2,
    ticketNumber: 'TKT-2026-002',
    subject: 'Main Cash Counter Porcelain Tile Cracking & Trip Hazard',
    branchName: 'UBL Clifton Block 5 Branch',
    priority: 'MEDIUM',
    status: 'ASSIGNED',
  },
  {
    id: 3,
    ticketNumber: 'TKT-2026-003',
    subject: 'Customer Waiting Area Wall Dampness & Paint Peeling',
    branchName: 'UBL Main Branch (Head Office)',
    priority: 'LOW',
    status: 'WORK_IN_PROGRESS',
  },
];

export const DashboardPage: React.FC<DashboardPageProps> = ({ onNavigate, mobileMode }) => {
  const { currentRole, currentUser } = useAuth();
  const [stats, setStats] = useState({
    totalProjects: 3,
    inProgressProjects: 2,
    openTickets: 2,
    completedProjects: 1,
    receivables: 1250000,
    totalRevenue: 4020000,
    actualCost: 2290000,
    totalProfit: 1730000,
  });

  const [projectsList, setProjectsList] = useState<any[]>(DEFAULT_PROJECTS);
  const [ticketsList, setTicketsList] = useState<any[]>(DEFAULT_TICKETS);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch('/api/projects')
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null),
      fetch('/api/tickets')
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null),
    ])
      .then(([prjs, tkts]) => {
        if (Array.isArray(prjs) && prjs.length > 0) {
          setProjectsList(prjs);
        }
        if (Array.isArray(tkts) && tkts.length > 0) {
          setTicketsList(tkts);
        }
      })
      .catch((err) => console.warn('Dashboard fetch notice:', err))
      .finally(() => setLoading(false));
  }, []);

  const formatPKR = (val: number) => {
    return 'Rs. ' + Math.round(val).toLocaleString('en-PK');
  };

  // Site Engineer Mobile View
  if (mobileMode || currentRole === 'SITE_ENGINEER') {
    return (
      <div className="space-y-5 max-w-xl mx-auto pb-12">
        <div className="bg-[#0f2942] text-white p-5 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[11px] font-semibold text-amber-400 uppercase tracking-wider">
                Site Operations Portal
              </span>
              <h2 className="text-xl font-bold mt-0.5">{currentUser.name}</h2>
              <p className="text-xs text-slate-300 mt-1">Field Engineer • Karachi & Punjab Region</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-amber-400">
              <Smartphone className="w-6 h-6" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-white/10 text-center">
            <div className="bg-white/5 p-2 rounded-lg">
              <div className="text-lg font-bold text-amber-300">2</div>
              <div className="text-[11px] text-slate-300">Active Site Projects</div>
            </div>
            <div className="bg-white/5 p-2 rounded-lg">
              <div className="text-lg font-bold text-emerald-300">3</div>
              <div className="text-[11px] text-slate-300">Open Work Orders</div>
            </div>
          </div>
        </div>

        {/* Quick Field Actions */}
        <div>
          <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2.5 px-1">
            Site Engineer Actions
          </h3>
          <div className="grid grid-cols-2 gap-2.5">
            <button
              onClick={() => onNavigate('surveys')}
              className="flex items-center gap-3 p-3.5 bg-white rounded-xl border border-slate-200 shadow-2xs hover:border-blue-400 text-left transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-800">New Site Survey</div>
                <div className="text-[10px] text-slate-500">Record branch measurements</div>
              </div>
            </button>

            <button
              onClick={() => onNavigate('sitework')}
              className="flex items-center gap-3 p-3.5 bg-white rounded-xl border border-slate-200 shadow-2xs hover:border-blue-400 text-left transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center">
                <Hammer className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-800">Update Progress</div>
                <div className="text-[10px] text-slate-500">Tile, ceiling & paint %</div>
              </div>
            </button>

            <button
              onClick={() => onNavigate('inventory')}
              className="flex items-center gap-3 p-3.5 bg-white rounded-xl border border-slate-200 shadow-2xs hover:border-blue-400 text-left transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
                <Boxes className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-800">Issue Materials</div>
                <div className="text-[10px] text-slate-500">Cement bags, paint drums</div>
              </div>
            </button>

            <button
              onClick={() => onNavigate('completion')}
              className="flex items-center gap-3 p-3.5 bg-white rounded-xl border border-slate-200 shadow-2xs hover:border-blue-400 text-left transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-700 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-800">Completion Sign-off</div>
                <div className="text-[10px] text-slate-500">Joint UBL certificate</div>
              </div>
            </button>
          </div>
        </div>

        {/* Assigned Projects */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
              My Active Branch Projects
            </h3>
            <button
              onClick={() => onNavigate('projects')}
              className="text-xs text-blue-700 font-semibold"
            >
              View All
            </button>
          </div>

          <div className="space-y-3">
            {(Array.isArray(projectsList) ? projectsList : []).slice(0, 3).map((prj) => (
              <div
                key={prj.id}
                onClick={() => onNavigate('project-detail', String(prj.id))}
                className="p-3 bg-slate-50 rounded-lg border border-slate-100 cursor-pointer hover:border-blue-300 transition-all"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-blue-900">{prj.projectNumber}</span>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                    {prj.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="text-xs font-semibold text-slate-800 mt-1">{prj.projectName}</div>
                <div className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-0.5">
                  <Building2 className="w-3.5 h-3.5 text-slate-400" />
                  <span>{prj.branchName} ({prj.branchCode})</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Standard Executive & Operational Dashboard
  return (
    <div className="space-y-6 w-full max-w-none">
      {/* Top Header & Quick Action Buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 w-full">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">
            Operational Overview
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Naeem Builder ERP • UBL Branch Renovation &amp; Maintenance Operations
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
          <button
            onClick={() => onNavigate('here4u')}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-lg text-xs font-bold shadow-2xs transition-colors"
          >
            <ActivitySquare className="w-3.5 h-3.5 text-slate-950" />
            <span>HERE4U Center</span>
          </button>

          <button
            onClick={() => onNavigate('tickets')}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg text-xs font-semibold shadow-2xs transition-colors"
          >
            <Plus className="w-3.5 h-3.5 text-slate-600" />
            <span>New Ticket</span>
          </button>

          <button
            onClick={() => onNavigate('projects')}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg text-xs font-semibold shadow-2xs transition-colors"
          >
            <FolderKanban className="w-3.5 h-3.5 text-blue-600" />
            <span>New Project</span>
          </button>

          <button
            onClick={() => onNavigate('consolidated-invoices')}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0f2942] hover:bg-[#153a5c] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
          >
            <Layers className="w-3.5 h-3.5 text-amber-400" />
            <span>Consolidated Invoice</span>
          </button>
        </div>
      </div>

      {/* Primary KPI Metric Cards - Full Width 4-Card Desktop Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 xl:gap-5 w-full">
        {/* Total Projects */}
        <div
          onClick={() => onNavigate('projects')}
          className="bg-white p-4 sm:p-5 rounded-xl border border-slate-200/80 shadow-2xs hover:border-blue-300 cursor-pointer transition-all min-w-0"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Total Projects
            </span>
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
              <FolderKanban className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2.5 flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-bold text-slate-900">
              {stats.totalProjects}
            </span>
            <span className="text-xs text-blue-700 font-medium">2 In-Progress</span>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">UBL Branch Renovations</p>
        </div>

        {/* Open Tickets */}
        <div
          onClick={() => onNavigate('tickets')}
          className="bg-white p-4 sm:p-5 rounded-xl border border-slate-200/80 shadow-2xs hover:border-amber-300 cursor-pointer transition-all min-w-0"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Maintenance Tickets
            </span>
            <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
              <Ticket className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2.5 flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-bold text-slate-900">
              {stats.openTickets}
            </span>
            <span className="text-xs text-amber-700 font-medium">Active Requests</span>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">From UBL Branch Network</p>
        </div>

        {/* Total Invoiced & Settled */}
        <div
          onClick={() => onNavigate('consolidated-invoices')}
          className="bg-white p-4 sm:p-5 rounded-xl border border-slate-200/80 shadow-2xs hover:border-emerald-300 cursor-pointer transition-all min-w-0"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Total Invoiced
            </span>
            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
              <Receipt className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2.5">
            <span className="text-xl sm:text-2xl font-bold text-emerald-700">
              {formatPKR(stats.totalRevenue)}
            </span>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">Consolidated &amp; Branch Bills</p>
        </div>

        {/* Gross Profit */}
        <div
          onClick={() => onNavigate('costing')}
          className="bg-white p-4 sm:p-5 rounded-xl border border-slate-200/80 shadow-2xs hover:border-purple-300 cursor-pointer transition-all min-w-0"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Gross Profit
            </span>
            <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-700 flex items-center justify-center shrink-0">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2.5">
            <span className="text-xl sm:text-2xl font-bold text-slate-900">
              {formatPKR(stats.totalProfit)}
            </span>
          </div>
          <p className="text-[11px] text-purple-700 font-medium mt-1">~43.0% Operating Margin</p>
        </div>
      </div>

      {/* Main Content: Active Projects (2fr) & Open Tickets (1fr, min 320px) */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)] xl:grid-cols-[minmax(0,2fr)_minmax(340px,1fr)] gap-6 w-full items-start">
        {/* Active Projects Table - Expands to occupy available width */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden min-w-0">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-sm sm:text-base font-bold text-slate-800">Active Projects &amp; Status</h2>
              <p className="text-xs text-slate-400">Live UBL renovation milestones and financials</p>
            </div>
            <button
              onClick={() => onNavigate('projects')}
              className="text-xs font-semibold text-blue-700 hover:text-blue-900 flex items-center gap-1 bg-blue-50/80 hover:bg-blue-100 px-2.5 py-1 rounded-md transition-colors"
            >
              <span>View All</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="overflow-x-auto w-full">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-slate-50 text-slate-500 border-b border-slate-100 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="py-3 px-4 w-2/5 min-w-[240px]">Project / Branch</th>
                  <th className="py-3 px-4 w-1/6 min-w-[120px]">PO Value</th>
                  <th className="py-3 px-4 w-1/4 min-w-[150px]">Cost vs Value</th>
                  <th className="py-3 px-4 w-auto min-w-[110px]">Status</th>
                  <th className="py-3 px-4 w-auto min-w-[90px] text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(Array.isArray(projectsList) ? projectsList : []).map((prj) => (
                  <tr key={prj.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="py-3.5 px-4 min-w-[240px]">
                      <div className="font-bold text-slate-900">{prj.projectNumber}</div>
                      <div className="text-slate-700 font-medium break-words mt-0.5">{prj.projectName}</div>
                      <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-1">
                        <Building2 className="w-3 h-3 shrink-0" />
                        <span className="truncate">{prj.branchName}</span>
                      </div>
                    </td>
                    <td className="py-3.5 px-4 font-semibold text-slate-800 whitespace-nowrap">
                      {formatPKR(Number(prj.contractValue))}
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="text-slate-700 whitespace-nowrap">Cost: {formatPKR(Number(prj.actualCost))}</div>
                      <div className="w-full max-w-[140px] bg-slate-200 h-1.5 rounded-full overflow-hidden mt-1.5">
                        <div
                          className="bg-blue-600 h-full rounded-full transition-all"
                          style={{
                            width: `${Math.min(
                              100,
                              (Number(prj.actualCost) / (Number(prj.contractValue) || 1)) * 100
                            )}%`,
                          }}
                        />
                      </div>
                    </td>
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      <span
                        className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                          prj.status === 'COMPLETED'
                            ? 'bg-emerald-100 text-emerald-800'
                            : prj.status === 'IN_PROGRESS'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {prj.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-right whitespace-nowrap">
                      <button
                        onClick={() => onNavigate('project-detail', String(prj.id))}
                        className="text-xs font-semibold text-blue-700 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-md transition-colors"
                      >
                        View Project
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tickets Feed - Sits comfortably on the right (min 320px) */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden flex flex-col min-w-0">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="text-sm sm:text-base font-bold text-slate-800">Recent Branch Tickets</h2>
              <p className="text-xs text-slate-400">Incoming maintenance requests</p>
            </div>
            <button
              onClick={() => onNavigate('tickets')}
              className="text-xs font-semibold text-blue-700 hover:text-blue-900 bg-blue-50/80 hover:bg-blue-100 px-2.5 py-1 rounded-md transition-colors"
            >
              All
            </button>
          </div>

          <div className="p-3 divide-y divide-slate-100 flex-1 overflow-y-auto">
            {(Array.isArray(ticketsList) ? ticketsList : []).map((tkt) => (
              <div
                key={tkt.id}
                onClick={() => onNavigate('tickets')}
                className="py-3 px-2.5 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-bold text-slate-900">{tkt.ticketNumber}</span>
                  <span
                    className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0 ${
                      tkt.priority === 'HIGH'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    {tkt.priority}
                  </span>
                </div>
                <div className="text-xs font-medium text-slate-800 mt-1 line-clamp-2 break-words">
                  {tkt.subject}
                </div>
                <div className="text-[11px] text-slate-500 mt-1.5 flex items-center justify-between gap-2">
                  <span className="truncate">{tkt.branchName}</span>
                  <span className="font-semibold text-blue-700 shrink-0">{tkt.status.replace('_', ' ')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
